import { ErrorBoundary } from 'react-error-boundary';
import { Box } from '@mui/material';
import { Map } from 'azure-maps-control';

import { useTranslation } from '@chr/online-shared-ui-localizations';

import { VisibilityBooking } from '@/common/apis/dtos';
import { testWebGlSupport } from '@/common/utilities/test-webgl-support';
import { EmptyContent } from '@/common/components/empty-content/EmptyContent';

import { MapThemeProvider } from './MapThemeProvider';
import { MapControls } from './MapControls';
import { OrderMarkersLayer } from './OrderMarkersLayer';
import { MappableStop } from './MapTypes';
import { TrackingMapTrafficLayer } from './TrackingMapTrafficLayer';
import { AzureTrackingMap } from './AzureTrackingMap';
import { MapKeyAndContentButtons } from './MapKeyAndContentButtons';
import { TrackingMapDisruptionsLayer } from './TrackingMapDisruptionsLayer';
import { TrackingMapWeatherLayer } from './TrackingMapWeatherLayer';

export interface MapDataLayerProps {
  stops: MappableStop[];
  booking: VisibilityBooking;
  onMapReady: (map: Map) => void | Promise<void>;
  map: Map;
}

export const MapErrorFallback = () => {
  const translate = useTranslation();

  return testWebGlSupport() ?
    (
      <EmptyContent
        title={translate('Failed To Load Tracking Map')}
        illustration="trafficCone"
        bodyText={translate('Something went wrong when trying to display the tracking map. Contact your account representative with the order number you tried to access.')}
      />
    ) : (
      <EmptyContent
        title={translate('Cannot Display Tracking Map')}
        illustration="barrier"
        bodyText={translate('Your system does not appear to support WebGL. The tracking map cannot be displayed without WebGL support. Please click below for troubleshooting options.')}
        link="https://get.webgl.org/"
        linkText="Troubleshooting"
      />
    );
};

export const TrackingMap = ({ stops, booking, map, onMapReady }: MapDataLayerProps) => {
  if (stops.length <= 0) {
    return null;
  }

  return (
    <Box className="no-print tracking-map">
      <ErrorBoundary FallbackComponent={MapErrorFallback}>
        <AzureTrackingMap onMapReady={onMapReady} map={map}>
          <MapThemeProvider>
            {map && (
              <>
                <MapControls data-testid="map-layer-controls" />
                <OrderMarkersLayer stops={stops} booking={booking} />
                <TrackingMapDisruptionsLayer />
                <TrackingMapTrafficLayer />
                <TrackingMapWeatherLayer />
                <MapKeyAndContentButtons />
              </>
            )}
          </MapThemeProvider>
        </AzureTrackingMap>
      </ErrorBoundary>
    </Box>
  );
};
